import { useEffect, useState, useRef } from 'react'
import { supabase } from '../services/supabase'
import PropTypes from 'prop-types'
import { AuthContext } from './auth.context'
import { useToast } from '../hooks/useToast'
import analyticsService from '../services/analytics/analytics'
import clientTokenService from '../services/clientTokenService'
import uuidService from '../services/uuidService'
import axios from 'axios'
import { isUserInitiatedLogin, clearLoginIntent } from '../utils/loginIntent'

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [clientToken, setClientToken] = useState(null)
  const [anonymousId, setAnonymousId] = useState('')
  const [conversationHistory, setConversationHistory] = useState([])
  const [currentConversationId, setCurrentConversationId] = useState(null)
  const [conversationStartTime, setConversationStartTime] = useState(null)
  const { showSuccess } = useToast()

  // Refs for synchronous access to prevent race conditions
  const currentConversationIdRef = useRef(null)
  const conversationStartTimeRef = useRef(null)

  // Use ref for showSuccess to avoid re-initialization when it changes
  const showSuccessRef = useRef(showSuccess)
  useEffect(() => {
    showSuccessRef.current = showSuccess
  }, [showSuccess])

  // Derived state
  const isAuthenticated = Boolean(user)
  const hasClientToken = Boolean(clientToken)

  useEffect(() => {
    // Initialize authentication and client token service
    const initializeAuth = async () => {
      try {
        // Initialize client token service (always available)
        const clientData = clientTokenService.initialize()
        setClientToken(clientData.token)
        setAnonymousId(clientData.anonymousId)

        // Check if this is a login flow or page reload
        const isLoginFlow = isUserInitiatedLogin()
        const isResumingConversation = localStorage.getItem('isResumingConversation') === 'true'

        let history = []
        if (isLoginFlow || isResumingConversation) {
          // Login flow OR conversation resume: Load history from localStorage
          history = clientTokenService.getConversationHistory() // Reads from localStorage
          setConversationHistory(history) // Sets React state → Updates UI
        } else {
          // Page reload/navigation (but NOT resume): Clear everything for fresh start
          clientTokenService.clearAllConversationData() // Clear localStorage and UUID keys
          setConversationHistory([]) // Clear React state → Empty UI
          clearConversationId() // Clear React state and localStorage UUID keys
        }

        // Load existing conversation ID from localStorage if available
        try {
          const existingConversationId = localStorage.getItem('et_ai_conversation_id')
          const existingStartTime = localStorage.getItem('et_ai_conversation_start_time')

          if (existingConversationId && existingStartTime) {
            const startTime = parseInt(existingStartTime, 10)

            // CRITICAL FIX: Always load existing conversation ID if present
            // This is essential for conversation restoration from database
            setCurrentConversationId(existingConversationId)
            setConversationStartTime(startTime)
            // Sync refs for immediate access
            currentConversationIdRef.current = existingConversationId
            conversationStartTimeRef.current = startTime
          } else if (history.length === 0) {
            // Only clear conversation data if no messages AND no stored conversation ID
            // This prevents clearing database conversation IDs during restoration
            if (!existingConversationId) {
              localStorage.removeItem('et_ai_conversation_id')
              localStorage.removeItem('et_ai_conversation_start_time')
            }
          }
        } catch (error) {
          console.warn('Failed to load conversation ID from localStorage:', error)
        }

        // Check for existing Supabase session (real authentication)
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (session?.user) {
          setUser(session.user)

          // Initialize analytics with authenticated user
          analyticsService.setUserId(session.user.id)
          analyticsService.setAnonymousId(clientData.anonymousId) // Keep anonymous ID for tracking
          analyticsService.setUserLevel(session.user.app_metadata?.user_level || 'guest')

          // Set user properties for analytics
          analyticsService.setUserProperties({
            email: session.user.email,
            created_at: session.user.created_at,
            user_level: session.user.app_metadata?.user_level || 'guest',
            was_anonymous: false,
            anonymous_id: clientData.anonymousId, // Include for conversion tracking
          })
        } else {
          // No authenticated session - initialize analytics with anonymous ID
          analyticsService.setAnonymousId(clientData.anonymousId)
          analyticsService.setUserLevel('anon')

          // Set anonymous user properties
          analyticsService.setUserProperties({
            user_level: 'anon',
            was_anonymous: true,
            session_start_time: clientData.sessionStartTime,
            anonymous_id: clientData.anonymousId,
          })
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth().catch((error) => {
      console.error('Failed to initialize authentication:', error)
      setLoading(false) // Ensure loading state is cleared even on failure
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null)

      // Handle sign in events
      if (event === 'SIGNED_IN' && session?.user) {
        const user = session.user
        const previousAnonymousId = clientTokenService.getAnonymousId()

        // Check if this is an actual login flow or just session restoration
        const isLoginFlow = isUserInitiatedLogin()

        const userLevel = user.app_metadata?.user_level || 'guest'

        // Auth linking debugging info available if needed

        // Only handle conversation history if this is an actual login (not page reload)
        if (!isLoginFlow) {
          // Clear login intent just in case
          clearLoginIntent()
          return
        }

        // NEW: Add auth linking integration for ALL users with login intent - attempt before any other processing
        const linkingResult = await handleAuthLinking(user, previousAnonymousId, userLevel)

        // Handle conversation history based on user level
        if (userLevel === 'guest') {
          // Guest users get fresh conversation history - clear existing conversations
          clientTokenService.clearAllConversationData()
          setConversationHistory([])
          clearConversationId()

          // Clear login intent for guest users
          clearLoginIntent()

          // Track conversation clearing for guest user
          analyticsService.trackEvent('guest_conversation_cleared', {
            user_id: user.id,
            anonymous_id: previousAnonymousId,
            user_level: userLevel,
          })
        } else {
          // Non-guest users (beta, etc.) preserve/restore conversation history

          // Use event-based approach instead of direct state manipulation
          const conversationHistory = clientTokenService.getConversationHistory()

          if (conversationHistory && conversationHistory.length > 0) {
            // Get existing conversation data
            const conversationId =
              localStorage.getItem('et_ai_conversation_id') || getOrCreateConversationId()
            const startTime = localStorage.getItem('et_ai_conversation_start_time') || Date.now()

            // Dispatch the conversation-resumed event
            const resumeEvent = new CustomEvent('conversation-resumed', {
              detail: {
                data: {
                  id: conversationId,
                  messages: conversationHistory,
                  startTime: parseInt(startTime, 10),
                },
                timestamp: Date.now(),
                operation: 'resume_conversation',
                source: 'login',
                userLevel,
                migrationId: previousAnonymousId, // For analytics tracking
              },
            })

            window.dispatchEvent(resumeEvent)
          }

          // Cleanup localStorage after successful linking (if applicable)
          await cleanupAfterSuccessfulLinking(user, previousAnonymousId, linkingResult)

          // Track user linking for analytics
          if (previousAnonymousId) {
            clientTokenService.trackUserLink(previousAnonymousId, user.id, {
              provider: user.app_metadata?.provider || 'email',
              conversationCount: conversationHistory?.length || 0,
            })
          }

          // Clear login intent immediately after using it
          clearLoginIntent()
        }

        // Set analytics user ID, anonymous ID, and user level
        analyticsService.setUserId(user.id)
        analyticsService.setAnonymousId(previousAnonymousId) // Keep for conversion tracking
        analyticsService.setUserLevel(userLevel)

        // Determine if this is a new user
        const createdAt = new Date(user.created_at)
        const now = new Date()
        const isNewUser = (now - createdAt) / 1000 < 10

        // Track authentication event
        const authMethod = user.app_metadata?.provider || 'email'
        analyticsService.trackAuth(authMethod, user.id, isNewUser)

        // Set user properties for analytics
        analyticsService.setUserProperties({
          email: user.email,
          created_at: user.created_at,
          user_level: userLevel,
          was_anonymous: Boolean(previousAnonymousId),
          previous_anonymous_id: previousAnonymousId, // Include for conversion tracking
          migrated_conversations:
            userLevel === 'guest' ? 0 : clientTokenService.getConversationHistory().length,
        })

        // Simple tracking: Link anonymous ID to authenticated user
        if (previousAnonymousId) {
          // Track the upgrade event with both IDs for journey tracking
          analyticsService.trackEvent('user_upgraded', {
            user_id: user.id, // Their new authenticated user ID
            anonymous_id: previousAnonymousId, // Their previous anonymous ID
            method: authMethod,
            user_level: userLevel,
            conversation_handling: userLevel === 'guest' ? 'cleared' : 'preserved',
          })

          // Set persistent user properties for future analytics
          analyticsService.setUserProperties({
            was_anonymous: true,
            previous_anonymous_id: previousAnonymousId,
          })
        }

        // Show appropriate welcome message based on user level and conversation handling
        const firstName = user.user_metadata?.first_name || user.email?.split('@')[0] || 'User'
        const capitalizedName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()

        if (userLevel === 'guest') {
          // Guest users always get fresh start message
          if (isNewUser) {
            showSuccessRef.current(
              `Welcome to Et Ai, ${capitalizedName}! You're starting with a fresh conversation.`
            )
          } else {
            showSuccessRef.current(
              `Welcome back, ${capitalizedName}! Starting fresh with a new conversation.`
            )
          }
        } else {
          // Non-guest users get migration-aware messages
          const conversationHistory = clientTokenService.getConversationHistory()

          // NEW: Check for linking result in welcome message
          if (previousAnonymousId && conversationHistory.length > 0) {
            if (linkingResult?.success) {
              showSuccessRef.current(
                `Account created successfully, ${capitalizedName}! Your conversation history has been saved and linked.`
              )
            } else {
              showSuccessRef.current(
                `Account created successfully, ${capitalizedName}! Your conversation history has been saved.`
              )
            }
          } else if (isNewUser) {
            showSuccessRef.current(`Welcome to Et Ai, ${capitalizedName}!`)
          } else {
            showSuccessRef.current(`Welcome back, ${capitalizedName}!`)
          }
        }
      } else if (event === 'SIGNED_OUT') {
        // Track sign out event
        analyticsService.trackSignOut()
        showSuccessRef.current('You have been signed out')

        // Clear session but preserve anonymous ID for continuity
        clientTokenService.clearSession(true)

        // Reinitialize client token service for new anonymous session
        const clientData = clientTokenService.initialize()
        setClientToken(clientData.token)
        setAnonymousId(clientData.anonymousId)
        setConversationHistory([])

        // Clear conversation UUID when signing out
        clearConversationId()

        // Set analytics back to anonymous tracking
        analyticsService.setUserId(null) // Clear authenticated user ID
        analyticsService.setAnonymousId(clientData.anonymousId)
        analyticsService.setUserLevel('anon')
        analyticsService.setUserProperties({
          user_level: 'anon',
          was_anonymous: true,
          session_start_time: clientData.sessionStartTime,
          anonymous_id: clientData.anonymousId,
        })
      }
    })

    // Add cleanup for page navigation - only clear if NOT resuming a conversation OR during OAuth
    const handleBeforeUnload = () => {
      // Check for resume lock (conversation restoration from database)
      const resumeLock = localStorage.getItem('et_ai_resume_lock')

      // Check for login intent (OAuth redirect in progress)
      const hasActiveLoginIntent = isUserInitiatedLogin()
      // Don't clear data if:
      // 1. Resume lock is active (database conversation restoration), OR
      // 2. Login intent is active (OAuth redirect in progress)
      if (resumeLock || hasActiveLoginIntent) {
        // Preserving data due to resume lock or OAuth login in progress
      } else {
        // Clear conversation data per behavior matrix requirements
        localStorage.removeItem('et_ai_conversation_id')
        localStorage.removeItem('et_ai_conversation_start_time')
        localStorage.removeItem('conversationHistory')
        clientTokenService.clearAllConversationData()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Ref for tracking processed resume timestamps to prevent duplicates
  const lastResumeTimestamp = useRef(null)

  // Effect to detect custom window events for conversation resume
  useEffect(() => {
    const handleConversationResumed = (event) => {
      const resumeData = event.detail

      if (resumeData && resumeData.operation === 'resume_conversation') {
        // Prevent duplicate processing
        if (lastResumeTimestamp.current === resumeData.timestamp) {
          return
        }

        lastResumeTimestamp.current = resumeData.timestamp

        // Update conversation state with new data
        setConversationHistory(resumeData.data.messages || [])

        // Update conversation ID and start time if provided
        if (resumeData.data.id) {
          setCurrentConversationId(resumeData.data.id)
          currentConversationIdRef.current = resumeData.data.id
        }

        if (resumeData.data.startTime) {
          setConversationStartTime(resumeData.data.startTime)
          conversationStartTimeRef.current = resumeData.data.startTime
        }

        // Analytics tracking based on source
        analyticsService.trackEvent('conversation_resumed', {
          source: resumeData.source || 'unknown',
          conversation_id: resumeData.data.id,
          message_count: resumeData.data.messages?.length || 0,
          user_level: resumeData.userLevel || 'unknown',
        })
      }
    }

    // Listen for custom conversation resumed events
    window.addEventListener('conversation-resumed', handleConversationResumed)

    return () => {
      window.removeEventListener('conversation-resumed', handleConversationResumed)
    }
  }, [])

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      console.error('Supabase auth error:', {
        message: error.message,
        status: error.status,
        code: error.code,
        details: error,
      })
      throw error
    }
    return data
  }

  const signUpWithEmail = async (email, password, options = {}) => {
    // Construct the signup request with proper structure
    const signUpData = {
      email,
      password,
      options: {},
    }

    // Add user metadata if provided
    if (options.data) {
      signUpData.options.data = options.data
    }

    // Add timeout wrapper to prevent indefinite hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            'Signup request timed out. This may be due to email server configuration issues. Please try again or contact support.'
          )
        )
      }, 30000) // 30 second timeout
    })

    try {
      const { data, error } = await Promise.race([supabase.auth.signUp(signUpData), timeoutPromise])

      if (error) throw error

      // When email confirmation is required, Supabase returns data with user as null
      // We return a success indicator instead of null to avoid errors
      return data || { success: true }
    } catch (error) {
      // Log SMTP-related errors for debugging
      if (error.message && error.message.includes('timeout')) {
        console.error('Signup timeout - likely SMTP configuration issue:', error)
      }
      throw error
    }
  }

  const signOut = async () => {
    // Clear any login intent to ensure clean state
    clearLoginIntent()

    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const signInWithProvider = async (provider) => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) {
      console.error('OAuth error:', {
        message: error.message,
        status: error.status,
        code: error.code,
        details: error,
      })
      throw error
    }
    return data
  }

  // Function to update conversation history
  const updateConversationHistory = (messages) => {
    // Filter and clean messages for storage with enhanced validation
    const messagesToStore = messages.map((message) => {
      if (message.parts) {
        // Parts-based message - validate and clean for storage
        const validatedParts = validateAndCleanParts(message.parts)

        const cleanedMessage = {
          ...message,
          parts: validatedParts, // Preserve validated parts array for storage (source of truth)
          // Remove temporary display state fields
          isProgress: false, // Should always be false when stored
          currentStatus: undefined, // Should be undefined when stored
          statusType: undefined, // Should be undefined when stored
          // Note: content field not stored, constructed from parts when needed for display

          // Preserve essential metadata for restoration
          lastUpdated: message.lastUpdated || new Date().toISOString(),
          streamingDuration: message.streamingDuration || 0,
          isError: message.isError || false,
        }

        // Validate component metadata preservation
        if (process.env.NODE_ENV === 'development') {
          const validation = validateComponentMetadataPreservation(cleanedMessage)
          if (!validation.valid) {
            console.warn('Component metadata validation failed for message:', validation.errors)
          }
          if (validation.warnings.length > 0) {
            console.warn('Component metadata validation warnings:', validation.warnings)
          }
        }

        return cleanedMessage
      } else {
        // Legacy message format - pass through unchanged for backward compatibility
        return message
      }
    })

    setConversationHistory(messagesToStore)
    const conversationId = getOrCreateConversationId()
    clientTokenService.saveConversationWithUUID(conversationId, messagesToStore)
  }

  /**
   * Validates and cleans parts array for storage, ensuring proper structure preservation.
   * @param {Array} parts - Array of message parts
   * @returns {Array} Validated and cleaned parts array
   */
  const validateAndCleanParts = (parts) => {
    if (!Array.isArray(parts)) {
      console.warn('Parts is not an array, converting to empty array')
      return []
    }

    return parts
      .filter((part) => {
        // Filter out invalid parts
        if (!part || typeof part !== 'object') {
          console.warn('Invalid part found, filtering out:', part)
          return false
        }

        // Ensure part has required fields
        if (!part.message && !part.content) {
          console.warn('Part missing message content, filtering out:', part)
          return false
        }

        return true
      })
      .map((part) => {
        // Clean and validate each part
        const cleanedPart = {
          // Core fields
          type: part.type || 'response',
          message: part.message || part.content || '',
          timestamp: part.timestamp || new Date().toISOString(),
          order: typeof part.order === 'number' ? part.order : 0,

          // Component fields (preserve if present)
          isComponent: Boolean(part.isComponent),
          componentType: part.componentType || null,

          // UUID fields (map from SSE format to storage format)
          sentence_id: part.sentence_id || part.sentenceUUID || null,
          message_id: part.message_id || part.messageId || part.messageUUID || null,
          conversation_id:
            part.conversation_id || part.conversationId || part.conversationUUID || null,

          // Metadata preservation
          metadata: part.metadata || {},
        }

        // Preserve component data if it exists
        if (part.componentData) {
          cleanedPart.componentData = part.componentData
        }

        // Preserve component-specific fields
        const componentFields = [
          'title',
          'level',
          'text',
          'action',
          'url',
          'variant',
          'code',
          'language',
          'params',
          'displayText',
          'originalMarker',
        ]
        componentFields.forEach((field) => {
          if (part[field] !== undefined) {
            cleanedPart[field] = part[field]
          }
        })

        // Preserve clickable sentence data
        if (part.sentences && Array.isArray(part.sentences)) {
          cleanedPart.sentences = part.sentences
        }

        // Preserve all remaining fields that aren't display-only
        const preservedFields = ['components', 'isTLDR', 'referenceId', 'event_id', 'source']
        preservedFields.forEach((field) => {
          if (part[field] !== undefined) {
            cleanedPart[field] = part[field]
          }
        })

        return cleanedPart
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0)) // Ensure parts are ordered correctly
  }

  /**
   * Validates that component metadata is properly preserved for a message.
   * @param {Object} message - Message to validate
   * @returns {Object} Validation result with status and details
   */
  const validateComponentMetadataPreservation = (message) => {
    const result = {
      valid: true,
      errors: [],
      warnings: [],
      componentCount: 0,
      sentenceCount: 0,
    }

    if (!message || typeof message !== 'object') {
      result.valid = false
      result.errors.push('Message is not a valid object')
      return result
    }

    // Check if message has parts array
    if (!Array.isArray(message.parts)) {
      result.warnings.push('Message missing parts array')
      return result
    }

    // Validate each part
    message.parts.forEach((part, index) => {
      if (!part || typeof part !== 'object') {
        result.valid = false
        result.errors.push(`Part ${index} is not a valid object`)
        return
      }

      // Validate component parts
      if (part.isComponent) {
        result.componentCount++

        // Check required component fields
        if (!part.componentType) {
          result.errors.push(`Component part ${index} missing componentType`)
          result.valid = false
        }

        // Validate component-specific metadata
        switch (part.componentType) {
          case 'heading':
            if (!part.title && !part.displayText) {
              result.warnings.push(`Heading component ${index} missing title/displayText`)
            }
            if (
              part.level &&
              (typeof part.level !== 'number' || part.level < 1 || part.level > 6)
            ) {
              result.warnings.push(`Heading component ${index} has invalid level: ${part.level}`)
            }
            break

          case 'button':
            if (!part.text && !part.displayText) {
              result.warnings.push(`Button component ${index} missing text/displayText`)
            }
            break

          case 'link':
            if (!part.url) {
              result.warnings.push(`Link component ${index} missing URL`)
            }
            if (!part.text && !part.displayText) {
              result.warnings.push(`Link component ${index} missing text/displayText`)
            }
            break

          case 'clickable':
            if (part.sentences && Array.isArray(part.sentences)) {
              result.sentenceCount += part.sentences.length

              part.sentences.forEach((sentence, sentenceIndex) => {
                if (!sentence.id && !sentence.sentence_id) {
                  result.warnings.push(
                    `Clickable component ${index}, sentence ${sentenceIndex} missing ID`
                  )
                }
                if (!sentence.text) {
                  result.errors.push(
                    `Clickable component ${index}, sentence ${sentenceIndex} missing text`
                  )
                  result.valid = false
                }
              })
            }
            break
        }

        // Check for original marker preservation
        if (part.originalMarker && !part.originalMarker.startsWith('[[')) {
          result.warnings.push(`Component part ${index} has malformed originalMarker`)
        }
      }

      // Validate UUID preservation
      if (part.sentence_id && typeof part.sentence_id !== 'string') {
        result.errors.push(`Part ${index} has invalid sentence_id type`)
        result.valid = false
      }

      if (part.message_id && typeof part.message_id !== 'string') {
        result.errors.push(`Part ${index} has invalid message_id type`)
        result.valid = false
      }

      // Validate order preservation
      if (typeof part.order !== 'number') {
        result.errors.push(`Part ${index} missing or invalid order field`)
        result.valid = false
      }

      // Validate timestamp preservation
      if (!part.timestamp) {
        result.warnings.push(`Part ${index} missing timestamp`)
      }

      // Validate metadata preservation
      if (part.metadata && typeof part.metadata !== 'object') {
        result.errors.push(`Part ${index} has invalid metadata type`)
        result.valid = false
      }
    })

    return result
  }

  // Function to get or create conversation ID
  const getOrCreateConversationId = () => {
    // CRITICAL FIX: Check for resume lock FIRST before any other logic
    const resumeLock = localStorage.getItem('et_ai_resume_lock')
    const resumeId = localStorage.getItem('et_ai_resume_conversation_id')

    if (resumeLock === 'true' && resumeId) {
      // We're in a resume flow, use the database ID

      // Update refs to match the database ID
      currentConversationIdRef.current = resumeId

      // Sync with React state
      setCurrentConversationId(resumeId)

      // Also sync start time if available
      const existingStartTime = localStorage.getItem('et_ai_conversation_start_time')
      if (existingStartTime) {
        const startTime = parseInt(existingStartTime, 10)
        conversationStartTimeRef.current = startTime
        setConversationStartTime(startTime)
      }

      return resumeId
    }

    // Check ref first for immediate synchronous access
    if (currentConversationIdRef.current) {
      return currentConversationIdRef.current
    }

    // Fallback to state (in case ref is not yet synced)
    if (currentConversationId) {
      currentConversationIdRef.current = currentConversationId
      return currentConversationId
    }

    // Check for existing conversation ID in localStorage
    try {
      const existingConversationId = localStorage.getItem('et_ai_conversation_id')
      const existingStartTime = localStorage.getItem('et_ai_conversation_start_time')

      if (existingConversationId && existingStartTime) {
        const startTime = parseInt(existingStartTime, 10)

        // Use the existing conversation ID (could be database ID or generated UUID)
        currentConversationIdRef.current = existingConversationId
        conversationStartTimeRef.current = startTime

        // Sync with React state
        setCurrentConversationId(existingConversationId)
        setConversationStartTime(startTime)

        return existingConversationId
      }
    } catch (error) {
      console.warn('Failed to load existing conversation ID from localStorage:', error)
    }

    // Only generate new conversation ID if not resuming and none exists
    const userId = user?.id || anonymousId
    const timestamp = conversationStartTimeRef.current || conversationStartTime || Date.now()
    const conversationId = uuidService.generateConversationId(userId, timestamp)

    // Update refs immediately for synchronous access
    currentConversationIdRef.current = conversationId
    conversationStartTimeRef.current = timestamp

    // Also update state for React rendering
    setCurrentConversationId(conversationId)
    setConversationStartTime(timestamp)

    // Persist conversation ID and start time in localStorage
    try {
      localStorage.setItem('et_ai_conversation_id', conversationId)
      localStorage.setItem('et_ai_conversation_start_time', timestamp.toString())
    } catch (error) {
      console.warn('Failed to save conversation ID to localStorage:', error)
    }

    return conversationId
  }

  // Function to clear conversation UUID (used when clearing messages)
  const clearConversationId = () => {
    // Clear refs immediately
    currentConversationIdRef.current = null
    conversationStartTimeRef.current = null

    // Clear state
    setCurrentConversationId(null)
    setConversationStartTime(null)

    // Remove from localStorage
    try {
      localStorage.removeItem('et_ai_conversation_id')
      localStorage.removeItem('et_ai_conversation_start_time')
    } catch (error) {
      console.warn('Failed to clear conversation ID from localStorage:', error)
    }
  }

  // Function to generate message ID
  const generateMessageId = (role) => {
    const conversationId = getOrCreateConversationId()
    const timestamp = Date.now()
    return uuidService.generateMessageId(conversationId, timestamp, role)
  }

  // Function to get current client token (refresh if needed)
  const getCurrentToken = async () => {
    // If we're authenticated, use the Supabase session token
    if (user) {
      const { data } = await supabase.auth.getSession()
      return data?.session?.access_token || null
    }

    // For anonymous users, use the client token
    const token = clientTokenService.refreshTokenIfNeeded()
    if (token && token !== clientToken) {
      setClientToken(token)
    }
    return token || clientToken || clientTokenService.getCurrentToken()
  }

  /**
   * Check if auth linking should be attempted for this user
   * @param {Object} user - Supabase user object
   * @param {string} previousAnonymousId - Anonymous ID to link
   * @param {string} _userLevel - User level from app_metadata (for logging only)
   * @returns {boolean} True if linking should be attempted
   */
  const shouldAttemptAuthLinking = (user, previousAnonymousId, _userLevel) => {
    // Must have previous anonymous ID to link
    if (!previousAnonymousId) {
      return false
    }

    // Must have valid user ID
    if (!user?.id) {
      return false
    }

    // Must not be an anonymous user (shouldn't happen, but safety check)
    if (user.is_anonymous === true || user.app_metadata?.is_anonymous === true) {
      return false
    }

    // Check for valid anonymous ID format
    if (!previousAnonymousId.startsWith('anon_')) {
      return false
    }

    // NOTE: No user level filtering - ALL authenticated users with login intent get linking
    // Backend determines what data gets linked based on user permissions
    return true
  }

  /**
   * Handle auth linking for eligible users during login flow
   * @param {Object} user - Supabase user object
   * @param {string} previousAnonymousId - Anonymous ID to link
   * @param {string} userLevel - User level from app_metadata
   * @returns {Object} Linking result with success status and metadata
   */
  const handleAuthLinking = async (user, previousAnonymousId, userLevel) => {
    // Check if auth linking should be attempted
    if (!shouldAttemptAuthLinking(user, previousAnonymousId, userLevel)) {
      return {
        attempted: false,
        reason: 'not_eligible',
        userLevel,
        hasAnonymousId: !!previousAnonymousId,
      }
    }

    try {
      // Call the auth link endpoint via Cloudflare Worker
      const response = await axios.post(
        '/api/auth/link',
        {
          anonymousId: previousAnonymousId,
        },
        {
          headers: {
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.status === 200) {
        const responseData = response.data

        // Track successful linking
        analyticsService.trackEvent('auth_link_success', {
          user_id: user.id,
          anonymous_id: previousAnonymousId,
          user_level: userLevel,
          linked_conversations: responseData.linkedConversations,
          migration_id: responseData.migrationId,
        })

        return {
          attempted: true,
          success: true,
          linkedConversations: responseData.linkedConversations,
          migrationId: responseData.migrationId,
        }
      }
    } catch (error) {
      // Track failed linking
      analyticsService.trackEvent('auth_link_error', {
        user_id: user.id,
        anonymous_id: previousAnonymousId,
        user_level: userLevel,
        error_message: error.message,
        error_status: error.response?.status,
      })

      // Handle specific error cases
      if (error.response?.status === 409) {
        // Already linked - treat as success
        return {
          attempted: true,
          success: true,
          alreadyLinked: true,
        }
      }

      // Don't block authentication flow on linking failure
      return {
        attempted: true,
        success: false,
        error: error.message,
      }
    }

    return { attempted: true, success: false }
  }

  /**
   * Clean up anonymous conversation data after successful linking
   * This is called after handleAuthLinking succeeds to remove local storage data
   * @param {Object} user - Authenticated user from Supabase
   * @param {string} previousAnonymousId - Previous anonymous ID for tracking
   * @param {Object} linkingResult - Result from handleAuthLinking
   */
  const cleanupAfterSuccessfulLinking = async (user, previousAnonymousId, linkingResult) => {
    // Only cleanup for non-guest users who had successful linking
    if (user.app_metadata?.user_level === 'guest' || !linkingResult?.success) {
      return
    }

    try {
      // Check for anonymous conversations in localStorage
      const anonymousDataKey = 'et_conversations_uuid'
      const anonymousData = localStorage.getItem(anonymousDataKey)

      let conversationIds = []

      // Check UUID-based conversations
      if (anonymousData) {
        try {
          const conversations = JSON.parse(anonymousData)
          conversationIds = Object.keys(conversations)
        } catch (error) {
          console.warn('Failed to parse UUID-based anonymous data:', error)
        }
      }

      if (conversationIds.length > 0) {
        // Clean up localStorage after successful linking/migration
        localStorage.removeItem(anonymousDataKey)
        localStorage.removeItem('et_ai_conversation_id')
        localStorage.removeItem('et_ai_conversation_start_time')

        // Track successful cleanup
        analyticsService.trackEvent('anonymous_data_cleanup', {
          user_id: user.id,
          anonymous_id: previousAnonymousId,
          conversation_count: conversationIds.length,
          user_level: user.app_metadata?.user_level,
          linked_conversations: linkingResult.linkedConversations,
          migration_id: linkingResult.migrationId,
        })
      }
    } catch (error) {
      console.error('Failed to cleanup anonymous data:', error)
      // Non-critical error - don't block the flow
    }
  }

  const value = {
    // Authentication state
    user,
    loading,
    isAuthenticated,
    hasClientToken,

    // Client token data
    clientToken,
    anonymousId,
    conversationHistory,

    // UUID data
    currentConversationId,
    conversationStartTime,

    // Authentication methods
    signInWithEmail,
    signUpWithEmail,
    signOut,
    signInWithProvider,

    // Utility functions
    updateConversationHistory,
    getCurrentToken,

    // UUID functions
    getOrCreateConversationId,
    clearConversationId,
    generateMessageId,

    // Backward compatibility (deprecated - use isAuthenticated)
    isAnonymous: !isAuthenticated,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
